import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ContentPageTheme, Player, ReviewPageConfig, ReviewPageConfigServices } from "@/services/bedrock";
import { publicWebOrigin, useDebounced } from "@/utils";
import { findTheme } from "../../components/sampleReviews";
import { useIframePreview } from "../../components/useIframePreview";
import { PLAYER_ID_PLACEHOLDER, usePlayerPreviewLoading } from "../../components/usePreviewPlayer";
import { buildReviewPagePreviewData } from "./reviewPagePreviewData";
import { getReviewPageSettings, reviewPagePreviewParams } from "./reviewPageSettings";

const TEXT_DEBOUNCE_MS = 400;

interface Args {
  config: ReviewPageConfig;
  themes: ContentPageTheme[];
  previewPlayer: Player | null;
}

const useReviewPagePreview = ({ config, themes, previewPlayer }: Args) => {
  const origin = publicWebOrigin();
  const iframeSrc = `${origin}/r/preview`;
  const playerGuid = previewPlayer?.guid;
  const settings = useMemo(() => getReviewPageSettings(config), [config]);
  const headline = useDebounced(settings.headline, TEXT_DEBOUNCE_MS);
  const subtitle = useDebounced(settings.subtitle, TEXT_DEBOUNCE_MS);
  const previewParams = useMemo(() => {
    const params = reviewPagePreviewParams({ ...settings, headline, subtitle });
    params.set("sentiment", "all");
    return params.toString();
  }, [settings, headline, subtitle]);

  const {
    data: playerData,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["reviewPagePublicPreview", playerGuid, previewParams],
    queryFn: () => ReviewPageConfigServices.getPublicPreview(playerGuid!, new URLSearchParams(previewParams)),
    enabled: !!playerGuid,
    placeholderData: previousData => previousData,
  });

  const isLoading = usePlayerPreviewLoading({ playerGuid, isFetching, isError, error });

  const samplePayload = useMemo(
    () => (playerGuid ? null : buildReviewPagePreviewData(settings, themes)),
    [playerGuid, settings, themes]
  );
  const { contentPageThemeId } = settings;

  const message = useMemo(() => {
    if (samplePayload) return { type: "REVIEW_PAGE", payload: samplePayload };
    if (isError || !playerData) return { type: "REVIEW_PAGE", payload: null };

    const selectedTheme = findTheme(themes, contentPageThemeId);
    return {
      type: "REVIEW_PAGE",
      payload: { ...playerData, ...(selectedTheme ? { theme: selectedTheme } : {}) },
    };
  }, [samplePayload, themes, contentPageThemeId, playerData, isError]);

  const { iframeRef } = useIframePreview({ src: iframeSrc, message });

  const playerSegment = playerGuid ? encodeURIComponent(playerGuid) : PLAYER_ID_PLACEHOLDER;
  const pageUrl = `${origin}/r/${playerSegment}`;

  return {
    iframeRef,
    iframeSrc,
    pageUrl,
    isSamplePreview: !playerGuid,
    isLoading,
    previewPlayerName: previewPlayer?.fullName || undefined,
  };
};

export { useReviewPagePreview };
